import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { hasPermission } from '@/types';
import { search as searchApi, type SearchHit, type SearchResult } from '@/api/endpoints';
import { Icon } from './Icon';
import { NotificationBell } from './NotificationBell';
import { ImpersonationBanner } from './ImpersonationBanner';
import { moveEntry, reconcileOrder, stepEntry, useNavOrder, type DropPlace } from './navOrder';

/**
 * Pojedyncza pozycja menu. Albo nagłówek sekcji (collapsible), albo link.
 *
 * Reguły widoczności:
 *  - requirePerm: user musi mieć dane uprawnienie z user.permissions (np. OFFER_VIEW);
 *    tablica = wystarczy JEDNO z wymienionych (np. mapa przeglądu: ujęcia ALBO prace)
 *  - sekcja jest renderowana TYLKO gdy ma >= 1 widoczny item (inaczej pomijamy nagłówek)
 *  - sekcja defaultCollapsed=true zwinięta przy pierwszym wejściu; stan zapisywany w localStorage
 */
type NavSection = {
  kind: 'section';
  id: string;
  label: string;
  /** Sekcja domyślnie zwinięta? (np. wszystkie podsekcje administracji) */
  defaultCollapsed?: boolean;
};
type NavLinkItem = {
  kind: 'link';
  id: string;
  to: string;
  label: string;
  icon: string;
  requirePerm?: string | string[];
  /** 'all' = wymagane WSZYSTKIE z requirePerm (gdy API ma koniunkcję). Domyślnie 'any'. */
  requirePermMode?: 'any' | 'all';
  /**
   * Dodatkowe słowa, po których wyszukiwarka menu ma trafiać w tę pozycję —
   * potoczne nazwy i nazwy modułów, których nie widać w etykiecie
   * („kosztorys" → Oferty, „urlop" → Kalendarz brygad, „ksef" → Faktury).
   */
  kw?: string;
};
type NavItem = NavSection | NavLinkItem;

const NAV: NavItem[] = [
  // ── Codzienna praca ────────────────────────────────────────────
  { kind: 'section', id: 'sec-praca', label: 'Praca' },
  { kind: 'link', id: 'dashboard', to: '/dashboard', label: 'Dashboard', icon: 'home', kw: 'pulpit start strona główna widgety' },
  // Codzienne „co dziś do zrobienia" zaraz pod pulpitem (2026-10). Użytkownik może tę kolejność
  // zmienić u siebie (Zmień kolejność menu) — NAV jest tylko kolejnością domyślną.
  { kind: 'link', id: 'tasks', to: '/tasks', label: 'Tablica zadań', icon: 'kanban', requirePerm: 'TASK_VIEW', kw: 'kanban todo przypisania priorytety' },
  { kind: 'link', id: 'terminy', to: '/terminy', label: 'Terminy', icon: 'alarm', requirePerm: 'COMPLIANCE_VIEW', kw: 'przegląd bhp ubezpieczenie ważność przypomnienia' },
  { kind: 'link', id: 'kalendarz', to: '/kalendarz', label: 'Kalendarz brygad', icon: 'calendar', requirePerm: 'CALENDAR_VIEW', kw: 'wyjazdy urlopy grafik obecność' },
  { kind: 'link', id: 'klienci', to: '/klienci', label: 'Klienci', icon: 'users', requirePerm: 'CLIENT_VIEW', kw: 'kontrahent firma inwestor gmina nip' },
  { kind: 'link', id: 'intakes', to: '/intakes', label: 'Ujęcia', icon: 'droplet', requirePerm: 'INTAKE_VIEW', kw: 'studnia wodociąg pobór wody otwory' },
  { kind: 'link', id: 'karty', to: '/karty', label: 'Karty otworów', icon: 'core', requirePerm: 'BOREHOLE_CARD_VIEW', kw: 'profil wiercenia litologia warstwy zestawienie wyników' },
  { kind: 'link', id: 'studies', to: '/studies', label: 'Opracowania', icon: 'file-text', requirePerm: 'STUDY_VIEW', kw: 'operat dokumentacja projekt robót hydrogeologiczna' },
  { kind: 'link', id: 'prace', to: '/prace', label: 'Prace terenowe', icon: 'drill', requirePerm: 'FIELDWORK_VIEW', kw: 'roboty studnie wiertnica czyszczenie pompa brygada' },
  // „Zlecenia (roboty)", nie samo „Zlecenia": w module ofert status ORDERED wyświetla się jako
  // „Zlecenie", a to co innego — oferta oznaczona jako zlecona, nie zlecenie na roboty.
  { kind: 'link', id: 'zlecenia', to: '/zlecenia', label: 'Zlecenia (roboty)', icon: 'clipboard', requirePerm: 'FIELDWORK_VIEW', kw: 'zlecenie na roboty robocizna wykonawca' },
  { kind: 'link', id: 'prace-harmonogram', to: '/prace/harmonogram', label: 'Harmonogram prac', icon: 'gantt', requirePerm: 'FIELDWORK_VIEW', kw: 'gantt wykres terminarz plan robót' },
  { kind: 'link', id: 'analiza-sitowa', to: '/analiza-sitowa', label: 'Analiza sitowa / Obsypka', icon: 'mesh', requirePerm: 'SIEVE_VIEW', kw: 'filtr żwir granulacja szczelina dvgw uziarnienie' },
  { kind: 'link', id: 'ocena-ryzyka', to: '/ocena-ryzyka', label: 'Oceny ryzyka', icon: 'shield', requirePerm: 'RISK_VIEW', kw: 'art. 4f 4g 4i woda pitna zaopatrzenie zagrożenia' },
  { kind: 'link', id: 'analiza-ryzyka-flota', to: '/analiza-ryzyka-flota', label: 'Analizy ryzyka (art. 133)', icon: 'shield-alert', requirePerm: 'RISK133_VIEW', kw: 'strefa ochronna pośrednia wyssling teren ochrony' },
  { kind: 'link', id: 'asystent', to: '/asystent', label: 'Asystent AI', icon: 'sparkles', requirePerm: 'AI_ASSISTANT_USE', kw: 'czat pytania rag wiedza rozmowa' },
  // Osobna pozycja, nie podstrona: bez niej ekran zarządzania korpusami był osiągalny WYŁĄCZNIE
  // przez ręczne wpisanie adresu — a pusty asystent odsyłał do „Asystent AI → Korpusy", czyli do
  // ścieżki, której w menu nie było.
  { kind: 'link', id: 'asystent-korpusy', to: '/asystent/korpusy', label: 'Korpusy wiedzy', icon: 'archive', requirePerm: 'AI_CORPUS_MANAGE', kw: 'rag baza wiedzy indeks pliki asystenta' },

  // ── Firma (Kadry, Pojazdy) ──────────────────────────────────────
  { kind: 'section', id: 'sec-firma', label: 'Firma' },
  { kind: 'link', id: 'kadry', to: '/kadry', label: 'Kadry', icon: 'id-card', requirePerm: 'HR_VIEW', kw: 'hr pracownicy umowy badania szkolenia płace' },
  { kind: 'link', id: 'urlopy', to: '/urlopy', label: 'Plan urlopów', icon: 'sun', requirePerm: 'HR_LEAVE_VIEW', kw: 'wolne wypoczynkowy na żądanie zaległy saldo' },
  { kind: 'link', id: 'dwutygodniowka', to: '/dwutygodniowka', label: 'Dwutygodniówka', icon: 'clock', requirePerm: 'HR_VIEW', kw: 'czas pracy godziny nadgodziny ewidencja rozliczenie' },
  { kind: 'link', id: 'pojazdy', to: '/pojazdy', label: 'Pojazdy i sprzęt', icon: 'truck', requirePerm: 'FLEET_VIEW', kw: 'flota auto samochód przegląd oc serwis' },

  // ── Finanse (Faktury KSeF + rozliczenia bankowe) ────────────────────────
  { kind: 'section', id: 'sec-finanse', label: 'Finanse' },
  { kind: 'link', id: 'faktury', to: '/faktury', label: 'Faktury', icon: 'receipt', requirePerm: 'FINANCE_VIEW', kw: 'ksef sprzedaż zakup płatności vat' },
  { kind: 'link', id: 'bank', to: '/bank', label: 'Bank / rozliczenia', icon: 'landmark', requirePerm: 'FINANCE_BANK_VIEW', kw: 'erste przelew biling rekoncyliacja wyciąg' },
  { kind: 'link', id: 'subskrypcje', to: '/subskrypcje', label: 'Subskrypcje i opłaty', icon: 'repeat', requirePerm: 'FINANCE_VIEW', kw: 'oc firmy podatek licencja abonament cykliczne' },

  // ── Oferty i umowy (cała sekcja gate'owana OFFER_VIEW = ukryta dla geologa) ─────
  { kind: 'section', id: 'sec-oferty', label: 'Oferty i umowy' },
  { kind: 'link', id: 'oferty', to: '/oferty', label: 'Oferty', icon: 'tag', requirePerm: 'OFFER_VIEW', kw: 'wycena kosztorys ryczałt zakres prac zlecone' },
  { kind: 'link', id: 'oferta-wizard', to: '/oferta-wizard', label: 'Kreator oferty', icon: 'wand', requirePerm: 'OFFER_CREATE', kw: 'nowa oferta wycena kosztorys warianty' },
  { kind: 'link', id: 'contract-wizard', to: '/contract-wizard', label: 'Kreator umów', icon: 'contract', requirePerm: 'CONTRACT_CREATE', kw: 'nowa umowa aneks kontrakt' },

  // ── Mapy ────────────────────────────────────────────────────────
  { kind: 'section', id: 'sec-mapy', label: 'Mapy' },
  // Jedna mapa zamiast dwóch (2026-08): „Mapa prac" i „Mapa przeglądu" rysowały tę samą pinezkę
  // klienta w dwóch różnych miejscach. `kw` niesie słowa obu dawnych pozycji, żeby szukajka menu
  // wciąż trafiała na „mapę prac".
  { kind: 'link', id: 'mapa-przeglad', to: '/mapa-przeglad', label: 'Mapa przeglądu', icon: 'map', requirePerm: ['INTAKE_VIEW', 'FIELDWORK_VIEW'], kw: 'gis ujęcia klienci punkty mapa prac roboty studnie stan terenowe' },
  { kind: 'link', id: 'mapy', to: '/mapy', label: 'Generowanie map', icon: 'layers', requirePerm: 'MAPS_DOWNLOAD', kw: 'wms wmts warstwy eksport podkład geoportal' },
  { kind: 'link', id: 'dopasowanie-map', to: '/dopasowanie-map', label: 'Dopasowanie map', icon: 'map-pin', requirePerm: 'GEOREF_VIEW', kw: 'georeferencja raster skan pdf kalibracja punkty kontrolne układ pl-1992 pl-2000 wycięcie' },

  // ── AI i archiwum ───────────────────────────────────────────────
  { kind: 'section', id: 'sec-ai', label: 'AI i archiwum' },
  { kind: 'link', id: 'ai', to: '/ai-editor', label: 'Edytor AI', icon: 'pen-ai', requirePerm: 'AI_GENERATE', kw: 'generowanie tekstu redakcja operatu' },
  { kind: 'link', id: 'drive', to: '/drive', label: 'Google Drive', icon: 'cloud', requirePerm: 'DRIVE_VIEW', kw: 'archiwum pliki dysk załączniki' },

  // ── Raporty ─────────────────────────────────────────────────────
  { kind: 'section', id: 'sec-raporty', label: 'Raporty' },
  { kind: 'link', id: 'raporty-oferty', to: '/raporty/oferty', label: 'Oferty', icon: 'chart-bar', requirePerm: ['REPORTS_VIEW', 'OFFER_VIEW', 'PRICING_VIEW'], requirePermMode: 'all', kw: 'raport statystyki skuteczność zestawienie' },
  { kind: 'link', id: 'raporty-opracowania', to: '/raporty/opracowania', label: 'Wykonane opracowania', icon: 'chart-pie', requirePerm: ['REPORTS_VIEW', 'STUDY_VIEW'], requirePermMode: 'all', kw: 'raport statystyki produkcja zestawienie' },

  // ── Mój profil ──────────────────────────────────────────────────
  { kind: 'section', id: 'sec-system', label: 'Mój profil' },
  { kind: 'link', id: 'settings', to: '/settings', label: 'Ustawienia konta', icon: 'lock', kw: 'hasło bezpieczeństwo dane osobowe profil 2FA RODO MCP agenci tokeny Claude zdalne' },
  { kind: 'link', id: 'audit', to: '/audit', label: 'Log audytu', icon: 'history', requirePerm: 'ADMIN_AUDIT', kw: 'historia zmian dziennik ślad' },

  // ── Administracja ── każdy link gate'owany uprawnieniem z macierzy (ADMIN_*), a nie sztywną rolą,
  // dzięki czemu widoczność wynika z macierzy i działa też dla nowych ról (Faza 1).
  // Sekcja pokazuje się gdy widoczna jest ≥1 pozycja poniżej.
  { kind: 'section', id: 'sec-admin', label: 'Administracja', defaultCollapsed: true },
  { kind: 'link', id: 'admin-uzytkownicy', to: '/admin/uzytkownicy', label: 'Użytkownicy i uprawnienia', icon: 'user-shield', requirePerm: ['ADMIN_USERS', 'ADMIN_PERMISSIONS'], kw: 'role macierz konta dostęp impersonacja' },
  { kind: 'link', id: 'admin-konfiguracja', to: '/admin/konfiguracja', label: 'Konfiguracja', icon: 'sliders', requirePerm: 'ADMIN_CONFIG', kw: 'ustawienia systemu parametry integracje' },
  { kind: 'link', id: 'admin-slowniki', to: '/admin/slowniki', label: 'Słowniki opracowań', icon: 'book-open', requirePerm: 'ADMIN_CONFIG', kw: 'typy kroki warstwy wymagane dokumenty' },
  { kind: 'link', id: 'admin-prace-terenowe', to: '/admin/prace-terenowe', label: 'Cennik prac terenowych', icon: 'drill', requirePerm: 'ADMIN_CONFIG', kw: 'regeneracja inspekcja tv diagnostyka remont rodzaje prac ceny stawki' },
  { kind: 'link', id: 'admin-ai', to: '/admin/ai', label: 'AI i wzorce', icon: 'chip', requirePerm: 'ADMIN_CONFIG', kw: 'prompt model szablony generowania' },
  { kind: 'link', id: 'admin-dane', to: '/admin/dane', label: 'Dane referencyjne', icon: 'database', requirePerm: 'ADMIN_CONFIG', kw: 'teryt jcwpd rzgw zarządy zlewni gminy' },
];

const COLLAPSED_STORAGE_KEY = 'hw_nav_collapsed_v1';
/** Czy pełne menu jest „przypięte" (rozwinięte na stałe) czy zwinięte do ikon. */
const PINNED_STORAGE_KEY = 'hw_nav_pinned_v1';

/** Domyślny zestaw zwiniętych sekcji — wszystkie z defaultCollapsed=true. */
function defaultCollapsed(): Set<string> {
  const out = new Set<string>();
  for (const it of NAV) {
    if (it.kind === 'section' && it.defaultCollapsed) out.add(it.id);
  }
  return out;
}

/**
 * Normalizacja do porównań w wyszukiwarce menu: małe litery bez polskich
 * znaków, żeby „ujecia" trafiało w „Ujęcia", a „zadan" w „Tablica zadań".
 * NFD rozkłada ą/ć/ę/…, ale NIE ł — stąd osobne podmiany przed rozkładem.
 */
function foldPl(s: string): string {
  return s.toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Jeden wiersz wyrenderowanego menu: nagłówek sekcji albo link. */
type MenuRow =
  | { kind: 'section'; id: string; label: string; isCollapsed: boolean; count: number }
  | { kind: 'link'; item: NavLinkItem; hitIdx: number; pre: string; mid: string; post: string };

export function Layout() {
  const { user, logout, impersonation } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Menu boczne jako szuflada na małych ekranach (<lg). Zamykamy po zmianie trasy.
  const [mobileNav, setMobileNav] = useState(false);

  // ── Wyszukiwarka pozycji menu ────────────────────────────────────────────
  // Filtruje SAMO MENU (nie dane — od tego jest GlobalSearch w pasku górnym).
  // Przy 40 pozycjach w 9 sekcjach przewijanie do celu było wolniejsze niż
  // wpisanie trzech liter.
  const [menuQuery, setMenuQuery] = useState('');
  // Indeks w płaskiej liście trafień, po której chodzi się strzałkami. −1 = brak.
  const [menuActiveIdx, setMenuActiveIdx] = useState(-1);
  const menuSearchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setMobileNav(false);
    setMenuQuery('');
    setMenuActiveIdx(-1);
  }, [loc.pathname]);

  // ── Kolejność menu (per użytkownik) ──────────────────────────────────────
  // `navOrder` to NAV w kolejności zapisanej na koncie; w trybie edycji pracujemy na
  // roboczej kopii `draftOrder`, utrwalanej dopiero przyciskiem „Zapisz".
  const { saved: savedNavOrder, save: saveNavOrder } = useNavOrder(user?.id);
  const navOrder = useMemo(() => reconcileOrder(NAV, savedNavOrder), [savedNavOrder]);
  const [draftOrder, setDraftOrder] = useState<NavItem[] | null>(null);
  const reordering = draftOrder != null;
  const order = draftOrder ?? navOrder;
  const [navSaving, setNavSaving] = useState(false);
  const [navSaveErr, setNavSaveErr] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ id: string; place: DropPlace } | null>(null);

  const startReorder = () => {
    setDraftOrder(navOrder);
    setNavSaveErr(null);
    setMenuQuery('');
    setMenuActiveIdx(-1);
  };
  const cancelReorder = () => { setDraftOrder(null); setDragId(null); setDropHint(null); setNavSaveErr(null); };
  const commitReorder = async () => {
    if (!draftOrder) return;
    const isDefault = draftOrder.every((it, i) => it.id === NAV[i].id);
    setNavSaving(true);
    setNavSaveErr(null);
    try {
      await saveNavOrder(isDefault ? null : draftOrder.map(it => it.id));
      setDraftOrder(null);
    } catch {
      setNavSaveErr('Nie udało się zapisać kolejności menu. Spróbuj ponownie.');
    } finally {
      setNavSaving(false);
    }
  };

  // ── Zwijanie menu do ikon (tylko desktop ≥lg) ────────────────────────────
  // pinned=true  → pełne menu (240px), treść obok (zachowanie domyślne).
  // pinned=false → wąska „szyna" ikon (64px); najechanie kursorem rozwija pełne
  //                menu jako overlay nad treścią (bez przesuwania układu).
  // Uwaga: całe zwijanie realizujemy klasami z prefiksem `lg:`, więc PONIŻEJ lg
  // (mobile) menu jest zawsze pełne — działa klasyczna szuflada. Nie używamy
  // matchMedia — jedynym źródłem prawdy o szerokości jest breakpoint Tailwinda.
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(PINNED_STORAGE_KEY);
      if (stored != null) return stored === '1';
    } catch { /* localStorage niedostępne */ }
    return true; // domyślnie przypięte — bez zmiany dla obecnych użytkowników
  });
  // Odpiętą szynę rozwija zaangażowanie kursorem (hover) LUB fokusem (klawiatura).
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);

  const togglePinned = () => {
    const next = !pinned;
    setPinned(next);
    // KLIK w przycisk fokusuje go, więc bez tego resetu `focused` (a przy myszy też
    // `hovering`) trzymałby menu rozwinięte po odpięciu — samo zjechanie kursorem nie
    // odbiera fokusu, więc szyna nie zwijała się bez odświeżenia. Reset czyni
    // przełączenie deterministycznym: przypięcie → pełne menu, odpięcie → od razu
    // szyna (hover/fokus rozwiną ją ponownie na żądanie).
    setHovering(false);
    setFocused(false);
    try { localStorage.setItem(PINNED_STORAGE_KEY, next ? '1' : '0'); }
    catch { /* localStorage może być wyłączone */ }
  };

  // showFull — czy pokazujemy pełną treść menu (etykiety, nagłówki sekcji).
  // Na desktopie: przypięte LUB aktywne (hover/fokus). Klasy poniżej są prefiksowane
  // `lg:`, więc na mobile (gdzie showFull też bywa false przy odpiętym menu) i tak
  // renderuje się pełne menu.
  const active = hovering || focused;
  const showFull = pinned || active || reordering;
  const railHide = showFull ? '' : 'lg:hidden';           // element znika na szynie
  const railCenter = showFull ? '' : 'lg:justify-center'; // wyśrodkowanie na szynie

  // Stan zwiniętych sekcji — persystowany w localStorage.
  // Click na nagłówku sekcji = toggle.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (stored) return new Set(JSON.parse(stored) as string[]);
    } catch { /* nieparsowalne — defaultuj */ }
    return defaultCollapsed();
  });

  const toggleSection = (sectionId: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      try { localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next])); }
      catch { /* localStorage może być wyłączone */ }
      return next;
    });
  };

  // Pre-compute: które itemy są widoczne. Jedynym kryterium jest macierz uprawnień —
  // pola adminOnly/roles (twarde nazwy ról) usunięte: nigdy nie były w NAV ustawione,
  // a zostawione kusiłyby, żeby obejść nimi macierz przy dodawaniu nowej pozycji.
  // Sekcja jest widoczna gdy ma >= 1 widoczny item — inaczej cały nagłówek pomijamy.
  const visibility = useMemo(() => {
    const itemVisible = new Map<string, boolean>();
    for (const it of order) {
      if (it.kind === 'link') {
        let ok = true;
        if (it.requirePerm) {
          const wymagane = Array.isArray(it.requirePerm) ? it.requirePerm : [it.requirePerm];
          const spelnia = it.requirePermMode === 'all'
            ? wymagane.every(p => user?.permissions?.includes(p))
            : wymagane.some(p => user?.permissions?.includes(p));
          if (!spelnia) ok = false;
        }
        itemVisible.set(it.id, ok);
      }
    }
    // Sekcja widoczna gdy któryś z items poniżej (do następnej sekcji) jest widoczny
    const sectionVisible = new Map<string, boolean>();
    let currentSection: string | null = null;
    let anyVisibleInCurrent = false;
    const flush = () => {
      if (currentSection) sectionVisible.set(currentSection, anyVisibleInCurrent);
    };
    for (const it of order) {
      if (it.kind === 'section') {
        flush();
        currentSection = it.id;
        anyVisibleInCurrent = false;
      } else if (currentSection && itemVisible.get(it.id)) {
        anyVisibleInCurrent = true;
      }
    }
    flush();
    return { itemVisible, sectionVisible };
  }, [order, user?.permissions]);

  // ── Złożenie listy wierszy menu (z filtrowaniem) ─────────────────────────
  // `rows` to gotowa do renderu sekwencja nagłówków i linków; `hits` to płaska
  // lista linków, po której chodzą strzałki. Podczas szukania stan zwinięcia
  // sekcji jest ignorowany — inaczej trafienie w zwiniętej „Administracji"
  // byłoby niewidoczne mimo dopasowania.
  const { rows: menuRows, hits: menuHits, searching } = useMemo(() => {
    // Przy zmianie kolejności szukanie jest wyłączone, a wszystkie sekcje rozwinięte —
    // inaczej nie byłoby widać, gdzie upuszcza się pozycję.
    const q = reordering ? '' : foldPl(menuQuery.trim());
    const searching = q.length > 0;
    const rows: MenuRow[] = [];
    const hits: NavLinkItem[] = [];

    let section: NavSection | null = null;
    let buffer: { item: NavLinkItem; pre: string; mid: string; post: string }[] = [];

    const flush = () => {
      if (!section || buffer.length === 0) return;
      const isCollapsed = searching || reordering ? false : collapsed.has(section.id);
      rows.push({ kind: 'section', id: section.id, label: section.label, isCollapsed, count: buffer.length });
      if (isCollapsed) return;
      for (const b of buffer) {
        rows.push({ kind: 'link', item: b.item, hitIdx: hits.length, pre: b.pre, mid: b.mid, post: b.post });
        hits.push(b.item);
      }
    };

    for (const it of order) {
      if (it.kind === 'section') {
        flush();
        buffer = [];
        section = visibility.sectionVisible.get(it.id) ? it : null;
        continue;
      }
      if (!section) continue;
      if (!visibility.itemVisible.get(it.id)) continue;

      // Bez zapytania: cała pozycja bez podświetlenia.
      if (!searching) {
        buffer.push({ item: it, pre: it.label, mid: '', post: '' });
        continue;
      }
      // Z zapytaniem: trafienie w etykietę (podświetlamy fragment) albo w słowa
      // kluczowe (podświetlać nie ma czego — dopasowanie jest poza etykietą).
      const at = foldPl(it.label).indexOf(q);
      if (at >= 0) {
        buffer.push({
          item: it,
          pre: it.label.slice(0, at),
          mid: it.label.slice(at, at + q.length),
          post: it.label.slice(at + q.length),
        });
      } else if (it.kw && foldPl(it.kw).includes(q)) {
        buffer.push({ item: it, pre: it.label, mid: '', post: '' });
      }
    }
    flush();

    return { rows, hits, searching };
  }, [collapsed, menuQuery, order, reordering, visibility]);

  const rowVisible = (id: string) =>
    visibility.itemVisible.get(id) ?? visibility.sectionVisible.get(id) ?? false;
  const step = (id: string, dir: -1 | 1) => setDraftOrder(o => o && stepEntry(o, id, dir, rowVisible));

  /** Właściwości przeciągania wiersza w trybie zmiany kolejności (natywne HTML5 DnD). */
  const dragProps = (id: string, isSection: boolean) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setDragId(id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!dragId || dragId === id) return;
      e.preventDefault();
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      let place: DropPlace = e.clientY < r.top + r.height / 2 ? 'before' : 'after';
      // Link upuszczony na nagłówek sekcji zawsze trafia na jej początek — „przed nagłówkiem"
      // wyglądałoby tak samo jak „za ostatnią pozycją poprzedniej sekcji", a to myli.
      const draggingSection = order.find(x => x.id === dragId)?.kind === 'section';
      if (isSection && !draggingSection) place = 'after';
      if (dropHint?.id !== id || dropHint.place !== place) setDropHint({ id, place });
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (dragId && dropHint) setDraftOrder(o => o && moveEntry(o, dragId, dropHint.id, dropHint.place));
      setDragId(null);
      setDropHint(null);
    },
    onDragEnd: () => { setDragId(null); setDropHint(null); },
  });

  const dropLine = (id: string) =>
    dropHint?.id === id
      ? (dropHint.place === 'before' ? ' shadow-[inset_0_2px_0_0_theme(colors.water.500)]' : ' shadow-[inset_0_-2px_0_0_theme(colors.water.500)]')
      : '';

  /** Strzałki góra/dół — dla dotyku i klawiatury, gdzie przeciąganie HTML5 nie działa. */
  const stepButtons = (id: string, label: string) => (
    <span className="ml-auto flex items-center gap-px shrink-0">
      <button type="button" onClick={() => step(id, -1)} title={`Przesuń „${label}" wyżej`} aria-label={`Przesuń „${label}" wyżej`}
              className="w-6 h-6 grid place-items-center rounded text-ink-400 hover:text-water-600 hover:bg-water-50">
        <Icon name="chevron-up" className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => step(id, 1)} title={`Przesuń „${label}" niżej`} aria-label={`Przesuń „${label}" niżej`}
              className="w-6 h-6 grid place-items-center rounded text-ink-400 hover:text-water-600 hover:bg-water-50">
        <Icon name="chevron-down" className="w-3.5 h-3.5" />
      </button>
    </span>
  );

  // Strzałka poza listę po zawężeniu wyników zostawiłaby zaznaczenie „w powietrzu".
  const activeHitIdx = menuActiveIdx >= 0 && menuActiveIdx < menuHits.length ? menuActiveIdx : -1;

  const clearMenuQuery = () => { setMenuQuery(''); setMenuActiveIdx(-1); };

  const onMenuSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMenuActiveIdx(i => Math.min(i + 1, menuHits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMenuActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Bez wyboru strzałkami Enter otwiera pierwsze trafienie — najczęstszy ruch
      // to „wpisz trzy litery i wejdź".
      const target = menuHits[activeHitIdx >= 0 ? activeHitIdx : 0];
      if (target) { nav(target.to); clearMenuQuery(); menuSearchRef.current?.blur(); }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (menuQuery) clearMenuQuery();
      else menuSearchRef.current?.blur();
    }
  };

  // ⌘K / Ctrl+K — kursor do wyszukiwarki menu. Na mobile otwiera szufladę,
  // a na odpiętej szynie rozwija ją (focus=true), bo inaczej pola nie widać.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key !== 'k' && e.key !== 'K') return;
      e.preventDefault();
      setMobileNav(true);
      setFocused(true);
      // Pole może się dopiero pojawić (szyna/szuflada) — fokus dopiero po
      // przerysowaniu Reacta. setTimeout, nie requestAnimationFrame: rAF nie
      // odpala się w karcie w tle, a skrót bywa wciśnięty tuż po powrocie na kartę.
      setTimeout(() => menuSearchRef.current?.focus(), 0);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={'min-h-screen lg:grid ' + (pinned ? 'lg:grid-cols-[240px_1fr]' : 'lg:grid-cols-[64px_1fr]')}>
      {/* Podkładka przyciemniająca pod szufladą (tylko mobile) */}
      {mobileNav && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileNav(false)} aria-hidden="true"/>
      )}
      <aside
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onFocus={() => setFocused(true)}
        // relatedTarget = element przejmujący fokus; jeśli wciąż w menu (Tab między
        // pozycjami) — nie zwijamy. Dopiero fokus poza menu kończy rozwinięcie.
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false); }}
        className={'bg-white text-ink-700 flex flex-col border-r border-ink-100 '
        + 'fixed inset-y-0 left-0 z-40 w-[240px] transition-[transform,width] duration-200 '
        + (mobileNav ? 'translate-x-0 ' : '-translate-x-full ')
        + 'lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 '
        + (showFull ? 'lg:w-[240px] ' : 'lg:w-[64px] ')
        // Rozwinięcie po najechaniu / sfokusowaniu odpiętej szyny = overlay nad treścią
        // (siatka trzyma 64px, aside wystaje na 240px z cieniem ponad main).
        + (!pinned && (active || reordering) ? 'lg:z-50 lg:shadow-2xl ' : 'lg:z-auto')}>
        {/* Header — fixed, nie scrolluje się. Wysokość h-14 = wysokość paska
            górnego, więc linie obu nagłówków schodzą się w jednym poziomie. */}
        <div className={'flex items-center gap-2.5 h-14 px-3 border-b border-ink-100 shrink-0 ' + railCenter}>
          <div className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-water-500 to-water-700 grid place-items-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2s7 8 7 13a7 7 0 1 1-14 0c0-5 7-13 7-13z"/>
            </svg>
          </div>
          <div className={'min-w-0 ' + railHide}>
            <div className="font-bold text-ink-900 text-[14px] -tracking-[0.01em] truncate">Hydro-Wolski</div>
            <div className="text-[10px] text-ink-300 uppercase tracking-[0.08em]">Portal · v3.0</div>
          </div>
          {/* Zmiana kolejności menu — w pełnym menu (także w szufladzie na mobile). */}
          <button
            type="button"
            onClick={reordering ? cancelReorder : startReorder}
            className={'ml-auto btn btn-ghost btn-icon hover:bg-ink-50 '
                       + (reordering ? 'text-water-600 bg-water-50 ' : 'text-ink-300 hover:text-ink-700 ') + railHide}
            title={reordering ? 'Zakończ bez zapisywania' : 'Zmień kolejność menu'}
            aria-label="Zmień kolejność menu"
            aria-pressed={reordering}
          >
            <Icon name="grip" />
          </button>
          {/* Przypnij / zwiń — widoczne tylko na ≥lg i tylko gdy menu jest rozwinięte */}
          <button
            type="button"
            onClick={togglePinned}
            className={'hidden ' + (showFull ? 'lg:inline-flex ' : '') + 'btn btn-ghost btn-icon text-ink-300 hover:text-ink-700 hover:bg-ink-50'}
            title={pinned ? 'Zwiń menu do ikon' : 'Przypnij menu'}
            aria-label={pinned ? 'Zwiń menu do ikon' : 'Przypnij menu'}
            aria-pressed={pinned}
          >
            <Icon name={pinned ? 'chevrons-left' : 'pin'} />
          </button>
        </div>

        {/* Wyszukiwarka pozycji menu. Na szynie zwija się do samej lupy, która
            rozwija menu i ustawia w polu kursor. */}
        {reordering ? (
          <div className="p-2.5 pb-2 shrink-0">
            <div className="rounded-lg border border-water-200 bg-water-50/60 p-2 text-[11px] text-ink-600">
              <div className="flex items-start gap-1.5">
                <Icon name="grip" className="w-3.5 h-3.5 mt-px text-water-600 shrink-0" />
                <span>Przeciągnij pozycję lub sekcję albo użyj strzałek. Kolejność zapisuje się na Twoim koncie.</span>
              </div>
              {navSaveErr && <div className="mt-1.5 text-red-600">{navSaveErr}</div>}
              <div className="mt-2 flex items-center gap-1.5">
                <button type="button" onClick={() => setDraftOrder([...NAV])}
                        className="btn btn-ghost btn-sm !px-1.5 text-[11px]" title="Przywróć kolejność domyślną">
                  Domyślna
                </button>
                <button type="button" onClick={cancelReorder} className="btn btn-secondary btn-sm ml-auto !px-2 text-[11px]">Anuluj</button>
                <button type="button" onClick={commitReorder} disabled={navSaving}
                        className="btn btn-primary btn-sm !px-2 text-[11px]">
                  <Icon name="check" className="w-3 h-3" /> {navSaving ? 'Zapis…' : 'Zapisz'}
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="p-2.5 pb-2 shrink-0">
          {/* Pole — jak reszta menu chowane WYŁĄCZNIE klasą `lg:`, żeby na mobile
              (gdzie szyny nie ma) działało też przy odpiętym menu. */}
          <div className={'flex items-center gap-2 h-[34px] px-2.5 rounded-lg border border-ink-100 bg-ink-50 transition '
                          + 'focus-within:bg-white focus-within:border-water-500 focus-within:ring-[3px] focus-within:ring-water-500/20 '
                          + railHide}>
            <Icon name="search" size={15} className="text-ink-300 shrink-0" />
            <input
              ref={menuSearchRef}
              value={menuQuery}
              onChange={e => { setMenuQuery(e.target.value); setMenuActiveIdx(-1); }}
              onKeyDown={onMenuSearchKeyDown}
              placeholder="Szukaj w menu…"
              aria-label="Szukaj w menu"
              className="flex-1 min-w-0 border-0 bg-transparent outline-none text-[13px] text-ink-800 placeholder:text-ink-300"
            />
            {menuQuery ? (
              <button type="button" onClick={() => { clearMenuQuery(); menuSearchRef.current?.focus(); }}
                      className="shrink-0 w-5 h-5 grid place-items-center rounded bg-ink-100 text-ink-500 hover:bg-ink-200"
                      title="Wyczyść" aria-label="Wyczyść wyszukiwanie">
                <Icon name="x" className="w-3 h-3" />
              </button>
            ) : (
              <kbd className="shrink-0 px-1.5 py-px rounded border border-ink-100 bg-white font-mono text-[10px] text-ink-300">⌘K</kbd>
            )}
          </div>
          {/* Zastępcza lupa — tylko na szynie (≥lg przy zwiniętym menu). */}
          <button
            type="button"
            onClick={() => { setFocused(true); setTimeout(() => menuSearchRef.current?.focus(), 0); }}
            className={'hidden ' + (showFull ? '' : 'lg:grid ')
                       + 'w-full h-[34px] place-items-center rounded-lg border border-ink-100 bg-ink-50 text-ink-400 hover:text-ink-700 hover:border-ink-200'}
            title="Szukaj w menu (⌘K)" aria-label="Szukaj w menu"
          >
            <Icon name="search" size={15} />
          </button>
        </div>
        )}

        {/* Nav items — scrollable gdy nie mieszczą się. Sekcje są collapsible.
            Renderujemy struktury dla OBU trybów, a widoczność sterujemy klasami `lg:`:
             • nagłówek sekcji — widoczny w pełnym menu, ukryty na szynie (railHide),
             • cienki separator — odwrotnie, tylko na szynie, wstawiany PRZED pierwszą
               ikoną kolejnej sekcji (brak „wiszących" separatorów). */}
        <nav className="flex-1 min-h-0 overflow-y-auto sidebar-scroll px-2 pb-2 flex flex-col gap-px">
          {menuRows.map((row, rowIdx) => {
            if (reordering) {
              const id = row.kind === 'section' ? row.id : row.item.id;
              const label = row.kind === 'section' ? row.label : row.item.label;
              if (row.kind === 'section') {
                return (
                  <div key={'s-' + id} {...dragProps(id, true)}
                       className={'flex items-center gap-1.5 shrink-0 mt-2 px-2 h-[30px] rounded-md cursor-move select-none '
                                  + 'text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-500 bg-ink-50 '
                                  + (dragId === id ? 'opacity-40 ' : '') + dropLine(id)}>
                    <Icon name="grip" className="w-3.5 h-3.5 text-ink-300 shrink-0" />
                    <span className="flex-1 truncate">{label}</span>
                    {stepButtons(id, label)}
                  </div>
                );
              }
              return (
                <div key={id} {...dragProps(id, false)}
                     className={'flex items-center gap-2 shrink-0 h-[32px] pl-2 pr-0.5 rounded-lg cursor-move select-none '
                                + 'text-[13px] font-medium text-ink-700 hover:bg-ink-50 '
                                + (dragId === id ? 'opacity-40 ' : '') + dropLine(id)}>
                  <Icon name="grip" className="w-3.5 h-3.5 text-ink-300 shrink-0" />
                  <Icon name={row.item.icon} className="text-ink-400 shrink-0" />
                  <span className="truncate">{label}</span>
                  {stepButtons(id, label)}
                </div>
              );
            }
            if (row.kind === 'section') {
              return (
                <button
                  key={'s-' + row.id}
                  type="button"
                  onClick={() => toggleSection(row.id)}
                  // Podczas szukania sekcje są rozwinięte na sztywno, więc zwijanie
                  // jest wyłączone — klikalny nagłówek, po którym nic się nie rusza,
                  // wyglądałby na zepsuty.
                  disabled={searching}
                  className={'items-center gap-1.5 w-full shrink-0 text-[10px] font-semibold uppercase tracking-[0.1em] '
                             + 'text-ink-400 px-2 pt-3 pb-1.5 text-left flex '
                             + (searching ? 'cursor-default ' : 'hover:text-ink-600 ') + railHide}
                >
                  <Icon name="chevron" className={'w-3 h-3 transition-transform ' + (row.isCollapsed ? '' : 'rotate-90')} />
                  <span className="flex-1">{row.label}</span>
                  {searching && (
                    <span className="font-mono text-[10px] font-medium tracking-normal text-ink-300">{row.count}</span>
                  )}
                </button>
              );
            }

            const item = row.item;
            const kbdActive = row.hitIdx === activeHitIdx;
            // Separator na szynie: przed pierwszą ikoną każdej sekcji poza pierwszą.
            const prev = menuRows[rowIdx - 1];
            const railDivider = prev?.kind === 'section' && rowIdx > 1;

            return (
              <div key={item.id} className="contents">
                {railDivider && (
                  <div className={'my-1.5 mx-2 shrink-0 border-t border-ink-100 hidden ' + (showFull ? '' : 'lg:block')} aria-hidden="true" />
                )}
                <NavLink
                  to={item.to}
                  title={showFull ? undefined : item.label}
                  aria-label={item.label}
                  onClick={clearMenuQuery}
                  className={({ isActive }) =>
                    'relative flex shrink-0 items-center gap-2.5 h-[34px] rounded-lg text-[13px] transition px-3 ' +
                    (showFull ? '' : 'lg:px-0 lg:justify-center ') +
                    (isActive
                      ? 'bg-water-50 text-ink-900 font-semibold '
                      : 'text-ink-700 font-medium hover:bg-ink-50 hover:text-ink-900 ') +
                    // Podświetlenie wyboru strzałkami — odróżnialne od pozycji bieżącej.
                    (kbdActive ? 'ring-1 ring-water-500 bg-water-50' : '')
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-water-500" aria-hidden="true" />
                      )}
                      <Icon name={item.icon} className={(isActive ? 'text-water-600' : 'text-ink-400') + ' shrink-0'} />
                      <span className={'truncate ' + railHide}>
                        {row.pre}
                        {row.mid && <mark className="bg-water-100 text-water-700 rounded-sm px-px">{row.mid}</mark>}
                        {row.post}
                      </span>
                    </>
                  )}
                </NavLink>
              </div>
            );
          })}

          {/* Wyraźne wejście w zmianę kolejności — sama ikona ⠿ w nagłówku była niezauważalna. */}
          {!reordering && !searching && (
            <button type="button" onClick={startReorder}
                    className={'mt-3 mx-1 flex shrink-0 items-center gap-2 h-[30px] px-2 rounded-lg border border-dashed border-ink-200 '
                               + 'text-[12px] text-ink-400 hover:text-water-700 hover:border-water-300 hover:bg-water-50 ' + railHide}>
              <Icon name="grip" className="w-3.5 h-3.5 shrink-0" />
              Zmień kolejność menu
            </button>
          )}

          {searching && menuHits.length === 0 && (
            <div className={'flex flex-col items-center gap-1.5 px-3 py-8 text-center ' + railHide}>
              <Icon name="search" size={22} className="text-ink-200" />
              <div className="text-[12px] text-ink-500">Brak pozycji dla „{menuQuery.trim()}"</div>
              <div className="text-[11px] text-ink-300">Klientów, ujęć i opracowań szukaj w pasku u góry.</div>
            </div>
          )}
        </nav>

        {/* Footer — user profile, fixed, zawsze widoczny */}
        <div className={'p-2.5 border-t border-ink-100 flex items-center gap-2.5 shrink-0 '
                        // Bursztynowe tło stopki dubluje baner: baner odjeżdża przy scrollu,
                        // a ten kafelek z tożsamością jest widoczny zawsze.
                        + (impersonation ? 'bg-amber-100 ' : 'bg-ink-50/60 ') + railCenter}>
          <div className={'w-[30px] h-[30px] shrink-0 rounded-full bg-gradient-to-br from-[#1e3a8a] to-water-500 '
                          + 'text-white font-semibold text-[12px] grid place-items-center '
                          + (impersonation ? 'ring-2 ring-amber-500' : '')}
               title={impersonation
                 ? `Pracujesz jako ${user?.fullName ?? ''} (Ty: ${impersonation.adminFullName})`
                 : (showFull ? undefined : (user?.fullName ?? 'Gość'))}>
            {user?.initials ?? '??'}
          </div>
          <div className={'flex-1 min-w-0 ' + railHide}>
            {impersonation && (
              <div className="text-amber-500 text-[10px] uppercase tracking-[0.08em] font-semibold">Pracujesz jako</div>
            )}
            <div className="text-ink-800 text-[12px] font-semibold truncate">{user?.fullName ?? 'Gość'}</div>
            <div className="text-ink-400 text-[10px]">{user?.roles?.[0] ?? ''}{user?.category && user.category !== 'NONE' ? ' · ' + user.category : ''}</div>
          </div>
          <button
            onClick={() => logout().then(() => nav('/login'))}
            className={'btn btn-ghost btn-icon text-ink-400 hover:text-ink-800 hover:bg-ink-100 ' + railHide}
            title="Wyloguj"
          >
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      <div className="flex flex-col min-w-0">
        {/* Baner + pasek górny tworzą JEDEN blok sticky. Baner musi zostać na ekranie przy
            scrollu — na mobile szuflada z tożsamością jest schowana, więc po przewinięciu
            zniknąłby jedyny sygnał „to nie Twoje konto". */}
        <div className="sticky top-0 z-20">
        <ImpersonationBanner />
        <div className="h-14 bg-white border-b border-ink-100 flex items-center px-4 sm:px-6 gap-3 sm:gap-4">
          <button type="button" onClick={() => setMobileNav(true)}
                  className="lg:hidden btn btn-ghost btn-icon text-ink-600 shrink-0" aria-label="Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h18M3 12h18M3 18h18"/>
            </svg>
          </button>
          <Breadcrumbs path={loc.pathname} />
          {/* Wyszukiwarka zajmuje dużo miejsca — chowamy na wąskich ekranach */}
          <div className="hidden md:contents"><GlobalSearch /></div>

          <div className="ml-auto flex items-center gap-2.5 shrink-0">
            <NotificationBell/>
            {hasPermission(user, 'INTAKE_CREATE') && (
              <NavLink to="/intake-wizard" className="btn btn-primary btn-sm">
                <Icon name="plus" /> <span className="hidden sm:inline">Nowe ujęcie</span>
              </NavLink>
            )}
          </div>
        </div>
        </div>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Breadcrumbs({ path }: { path: string }) {
  const crumbs: { label: string; to?: string }[] = [{ label: 'Hydro-Wolski', to: '/dashboard' }];
  const seg = path.split('/').filter(Boolean);
  if (seg[0]) {
    const labels: Record<string, string> = {
      dashboard: 'Pulpit', klienci: 'Klienci', intakes: 'Ujęcia', karty: 'Karty otworów', studies: 'Opracowania',
      prace: 'Prace terenowe', zlecenia: 'Zlecenia na roboty',
      'analiza-sitowa': 'Analiza sitowa / Obsypka',
      'asystent': 'Asystent AI',
      'korpusy': 'Korpusy wiedzy',
      'ocena-ryzyka': 'Oceny ryzyka',
      'analiza-ryzyka-flota': 'Analizy ryzyka (art. 133)',
      tasks: 'Tablica zadań', terminy: 'Terminy', kalendarz: 'Kalendarz brygad',
      kadry: 'Kadry', urlopy: 'Plan urlopów', pojazdy: 'Pojazdy i sprzęt', faktury: 'Faktury', bank: 'Bank / rozliczenia', subskrypcje: 'Subskrypcje i opłaty', dwutygodniowka: 'Dwutygodniówka', 'dwutygodniowka-masowo': 'Dwutygodniówki — masowo', mapy: 'Mapy', 'mapa-przeglad': 'Mapa przeglądu', 'ai-editor': 'Edytor AI',
      drive: 'Google Drive', 'intake-wizard': 'Kreator ujęcia',
      'contract-wizard': 'Kreator umów', oferty: 'Oferty',
      'oferta-wizard': 'Kreator oferty', raporty: 'Raporty', audit: 'Log audytu',
      admin: 'Administracja',
    };
    crumbs.push({ label: labels[seg[0]] ?? seg[0] });
  }
  if (seg[0] === 'admin' && seg[1] === 'zarzady-zlewni') {
    crumbs.push({ label: 'Zarządy Zlewni', to: '/admin/dane' });
    if (seg[2]) crumbs.push({ label: `#${seg[2]}` });
  } else if (seg[1]) {
    // drugi segment to zwykle id rekordu (#123), ale bywa podstroną nazwaną słownie —
    // wtedy „#przewodnik" wyglądałoby jak identyfikator, więc pokazujemy etykietę
    const podstrony: Record<string, string> = {
      przewodnik: 'Przewodnik', harmonogram: 'Harmonogram', mapa: 'Mapa',
      'prace-terenowe': 'Cennik prac terenowych',
    };
    crumbs.push({ label: podstrony[seg[1]] ?? `#${seg[1]}` });
  }
  return (
    <div className="flex items-center gap-1.5 text-[13px] text-ink-400">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={i} className="flex items-center gap-1.5">
            {c.to && !last ? (
              <NavLink to={c.to} className="text-ink-500 hover:text-ink-800">{c.label}</NavLink>
            ) : (
              <span className={last ? 'text-ink-800 font-medium' : 'text-ink-500'}>{c.label}</span>
            )}
            {!last && <span className="text-ink-300">/</span>}
          </span>
        );
      })}
    </div>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounce 250ms
  useEffect(() => {
    const h = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(h);
  }, [query]);

  // Wywołanie API gdy debounced query >= 2 znaki
  useEffect(() => {
    if (debounced.trim().length < 2) {
      setResult(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    searchApi.global(debounced.trim())
      .then(r => { if (!cancelled) { setResult(r); setLoading(false); } })
      .catch(() => { if (!cancelled) { setResult(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [debounced]);

  // Klik poza komponent → zamknij dropdown
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Płaska lista wszystkich trafień (do nawigacji klawiszami)
  const flatHits: SearchHit[] = [
    ...(result?.clients ?? []),
    ...(result?.intakes ?? []),
    ...(result?.studies ?? []),
  ];

  const goTo = (h: SearchHit) => {
    if (h.type === 'client') navigate('/klienci/' + h.id);
    else if (h.type === 'intake') navigate('/intakes/' + h.id);
    else if (h.type === 'study') navigate('/studies/' + h.id);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flatHits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && flatHits[activeIdx]) goTo(flatHits[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const totalHits = flatHits.length;
  const showDropdown = open && debounced.trim().length >= 2;

  return (
    <div ref={containerRef} className="flex-1 max-w-[480px] relative">
      <div className="flex items-center gap-2 bg-ink-50 border border-ink-100 px-2.5 py-1.5 rounded-md text-ink-400 focus-within:border-water-500 focus-within:bg-white">
        <Icon name="search" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Szukaj klientów, ujęć, opracowań… (min. 2 znaki)"
          className="flex-1 border-0 bg-transparent outline-none text-[13px] text-ink-800"
        />
        {query && (
          <button onClick={() => { setQuery(''); setOpen(false); }} className="text-ink-400 hover:text-ink-700" title="Wyczyść">
            <Icon name="x" className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-ink-100 rounded-md shadow-lg z-30 max-h-[480px] overflow-auto">
          {loading && <div className="p-3 text-[12px] text-ink-400">Szukam…</div>}
          {!loading && totalHits === 0 && (
            <div className="p-3 text-[12px] text-ink-400">Brak wyników dla „{debounced}".</div>
          )}
          {!loading && totalHits > 0 && (
            <>
              {result?.clients && result.clients.length > 0 && (
                <SearchSection title="Klienci" iconName="users" hits={result.clients} startIdx={0}
                               activeIdx={activeIdx} onPick={goTo}/>
              )}
              {result?.intakes && result.intakes.length > 0 && (
                <SearchSection title="Ujęcia" iconName="droplet" hits={result.intakes}
                               startIdx={result?.clients?.length ?? 0}
                               activeIdx={activeIdx} onPick={goTo}/>
              )}
              {result?.studies && result.studies.length > 0 && (
                <SearchSection title="Opracowania" iconName="file-text" hits={result.studies}
                               startIdx={(result?.clients?.length ?? 0) + (result?.intakes?.length ?? 0)}
                               activeIdx={activeIdx} onPick={goTo}/>
              )}
              <div className="text-[10px] text-ink-400 px-3 py-1.5 border-t border-ink-100 bg-ink-50/50">
                ↑ ↓ przewijanie · Enter — otwórz · Esc — zamknij
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SearchSection({ title, iconName, hits, startIdx, activeIdx, onPick }: {
  title: string;
  iconName: string;
  hits: SearchHit[];
  startIdx: number;
  activeIdx: number;
  onPick: (h: SearchHit) => void;
}) {
  return (
    <div className="border-b border-ink-100 last:border-b-0">
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider text-ink-500 bg-ink-50/50">
        <Icon name={iconName} className="w-3 h-3"/>
        {title}
        <span className="ml-auto font-mono">{hits.length}</span>
      </div>
      {hits.map((h, i) => {
        const idx = startIdx + i;
        const active = activeIdx === idx;
        return (
          <button
            key={`${h.type}-${h.id}`}
            onClick={() => onPick(h)}
            onMouseEnter={() => { /* mouse hover doesn't change activeIdx, kbd-only */ }}
            className={'w-full text-left px-3 py-2 hover:bg-water-50 transition border-l-[3px] ' +
              (active ? 'border-water-500 bg-water-50' : 'border-transparent')}
          >
            <div className="text-[13px] text-ink-900 font-semibold truncate">{h.title}</div>
            {h.subtitle && <div className="text-[11px] text-ink-500 truncate">{h.subtitle}</div>}
          </button>
        );
      })}
    </div>
  );
}
